import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';

// Mirrors the backend PaymentReportDto (api/payment/report). Defined locally rather than via
// generated Api.* types to avoid the type-generation step for this one report.
interface PaymentReportRow {
  Id: number;
  Status: string;
  VippsStatus: string;
  PaidByPhonenumber: string;
  PaymentMessageField: string;
  VippsReference: string;
  ProductName: string;
  Amount: number;
  DateModified: string;
  PayerEmail: string;
  DepartmentId: number | null;
}

type RowKey =
  | 'Status' | 'VippsStatus' | 'PayerEmail' | 'PaidByPhonenumber'
  | 'PaymentMessageField' | 'ProductName' | 'Amount' | 'VippsReference' | 'DateModified';

const COLUMNS: { key: RowKey; label: string }[] = [
  { key: 'Status',              label: 'Status' },
  { key: 'VippsStatus',         label: 'Vipps-status' },
  { key: 'PayerEmail',          label: 'Betalers e-post' },
  { key: 'PaidByPhonenumber',   label: 'Telefon' },
  { key: 'PaymentMessageField', label: 'Melding' },
  { key: 'ProductName',         label: 'Produkt' },
  { key: 'Amount',              label: 'Beløp' },
  { key: 'VippsReference',      label: 'Referanse' },
  { key: 'DateModified',        label: 'Sist endret' },
];

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
};

const tableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: '0.875rem', width: '100%' };
const thStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px', background: '#f0f0f0', textAlign: 'left', position: 'sticky', top: 0 };
const tdStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px' };
const filterRowStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' };
const filterLabelStyle: React.CSSProperties = { minWidth: 180, flex: '0 0 auto' };

const PaymentView: React.FC<RouteComponentProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<PaymentReportRow[]>([]);
  const [departments, setDepartments] = useState<Api.Department[]>([]);

  const [deptFilter, setDeptFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const [data, depts] = await Promise.all([
          RestService.get<PaymentReportRow[]>('/api/payment/report'),
          RestService.get<Api.Department[]>('/api/examregistration/GetDepartments'),
        ]);
        setRows(data ?? []);
        setDepartments(depts ?? []);
      } catch (e) {
        toast.error(`Kunne ikke laste betalinger: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const departmentName = (id?: number | null) => departments.find(d => d.Id === id)?.Name ?? '';

  // Filter options derived from the data so dropdowns never offer zero-result picks.
  const deptOptions = useMemo(() => {
    const ids = new Set(rows.map(r => r.DepartmentId).filter((v): v is number => v != null && v > 0));
    return departments
      .filter(d => ids.has(d.Id))
      .sort((a, b) => a.Name.localeCompare(b.Name, 'nb'));
  }, [rows, departments]);

  const statusOptions = useMemo(() => {
    const s = new Set(rows.map(r => r.Status).filter((v): v is string => !!v));
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'nb'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (deptFilter && String(r.DepartmentId ?? '') !== deptFilter) return false;
      if (statusFilter && r.Status !== statusFilter) return false;
      return true;
    });
  }, [rows, deptFilter, statusFilter]);

  const cellValue = (row: PaymentReportRow, key: RowKey): string => {
    switch (key) {
      case 'DateModified': return formatDateTime(row.DateModified);
      case 'Amount':       return row.Amount != null ? `${row.Amount} kr` : '';
      default: {
        const v = (row as any)[key];
        return v == null ? '' : String(v);
      }
    }
  };

  const hasActiveFilter = !!(deptFilter || statusFilter);

  return (
    <>
      <OKomBreadcrumb path='Betalinger'></OKomBreadcrumb>
      {isLoading && <OKomLoading text='Henter betalinger...' />}
      <div className="ods-margin-top-3 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <div className="ods-grid__column--12">
          <div style={filterRowStyle}>
            <label className="ods-input__label" style={filterLabelStyle}>Avdeling
              <div className="ods-select__select">
                <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                  <option value="">Alle avdelinger</option>
                  {deptOptions.map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                </select>
              </div>
            </label>

            <label className="ods-input__label" style={filterLabelStyle}>Status
              <div className="ods-select__select">
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="">Alle statuser</option>
                  {statusOptions.map(s => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
            </label>

            {hasActiveFilter && (
              <button
                type="button"
                className="ods-button ods-button--red"
                onClick={() => { setDeptFilter(''); setStatusFilter(''); }}
              >
                Nullstill filter
              </button>
            )}
          </div>
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3" style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Avdeling</th>
                {COLUMNS.map(c => (<th key={c.key} style={thStyle}>{c.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => (
                <tr key={r.Id}>
                  <td style={tdStyle}>{departmentName(r.DepartmentId)}</td>
                  {COLUMNS.map(c => (<td key={c.key} style={tdStyle}>{cellValue(r, c.key)}</td>))}
                </tr>
              ))}
              {filteredRows.length === 0 && !isLoading && (
                <tr>
                  <td style={tdStyle} colSpan={COLUMNS.length + 1}>
                    {rows.length === 0 ? 'Ingen betalinger.' : 'Ingen treff for valgte filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default withRouter(PaymentView);
